import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { validatePhone, formatPhoneE164 } from '../../../../utils/phone';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, PhotoDimensions, FileTypes, TreatmentOptions } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { createEmptyFirestoreDoc } from '../../../../data/createEmptyFirestoreDoc';
import { getUnattestedScript } from '../../../../data/treatment/getUnattestedScript';
import { Button } from '../../../../components/buttons/Button';
import { Heading } from '../../../../components/Heading';
// import { DatePicker } from 'antd';
import { formatDateForCalendar } from '../../../../utilities/formatDateForCalendar';
import { formatDateForStorage } from '../../../../utilities/formatDateForStorage';
import { validateAdultDob } from '../../../../utilities/validateAdultDob';
import { FieldInfo } from '../../../../components/FieldInfo';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

import { PhotoCamera2 } from '../../../../components/PhotoCamera2';

import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/statement/attestation')({
  component: AttestationPage,
});

function AttestationPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [scripHasError, setScripHasError] = React.useState(false);
  const [attested, setAttested] = React.useState(false);
  const [hasReader, setHasReader] = React.useState(false);
  const [readerSubmitted, setReaderSubmitted] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [takingPhoto, setTakingPhoto] = React.useState({
    readerIdentificationFront: false,
  });
  const [localCode, setLocalCode] = React.useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = React.useState(false);
  const { mutate, mutateAsync } = useUpdateChartSection();
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  // REFS TO PREVENT RACE CONDITIONS DURING CHAINED MUTATIONS
  const capturedAddReaderFormValuesRef = React.useRef<typeof addReaderForm.state.values | null>(null);
  const capturedScriptErrorFormValuesRef = React.useRef<typeof scriptErrorForm.state.values | null>(null);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  React.useEffect(() => {
    if (chart && chart.SELECTED_TREATMENT_OPTION && chart.SELECTED_TREATMENT_OPTION.code?.length > 0) {
      setLocalCode(chart.SELECTED_TREATMENT_OPTION.code);
    } else {
      setLocalCode('fullCode');
    }
  }, [chart]);

  // SET HAS READER FROM CHART
  React.useEffect(() => {
    if (chart?.SCRIPT_REVIEW?.speaker === 'reader' || chart?.SCRIPT_REVIEW?.speaker === 'hca') {
      setHasReader(true);
    }
  }, [chart]);

  const handleRemoveReader = async () => {
    if (!chart) return;
    if (isSaving) return; // Prevent double-submit
    setIsSaving(true);

    // Create a modified chart with speaker set to 'patient' so getUnattestedScript generates the correct script
    const chartWithoutReader = JSON.parse(JSON.stringify(chart)) as Chart;
    chartWithoutReader.SCRIPT_REVIEW = {
      ...chartWithoutReader.SCRIPT_REVIEW,
      speaker: 'patient',
    };

    // Regenerate script with patient as speaker
    const patientScript = getUnattestedScript({
      code: localCode as keyof typeof TreatmentOptions,
      chart: chartWithoutReader,
      patientInfo,
    });

    if (!(await clearSelectedTreatmentOption())) return;

    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: chart.SCRIPT_REVIEW?.scriptAttested || false,
            speaker: 'patient',
            reader: {
              email: '',
              fullName: '',
              phoneNumber: '',
            },
            readerIdentificationFrontURI: '',
            telePrompterScript: patientScript,
          },
        },
      },
      {
        onSuccess: () => {
          setHasReader(false); // Explicit false, not !hasReader
          setReaderSubmitted(false); // Reset so "Add Reader" button shows again
          addReaderForm.reset(); // Clear form values for fresh entry
          setIsSaving(false);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`);
          console.error(error);
          setIsSaving(false);
        },
      },
    );
  };

  // NOTE: MUTATIONS RUN SEQUENTIALLY TO ENSURE onSuccess CALLBACKS FIRE CORRECTLY.
  // PARALLEL MUTATIONS ON THE SAME useMutation HOOK CAUSE CALLBACK OVERWRITES.
  const handleAddReader = async () => {
    if (!chart) return;
    if (isSaving) return; // Prevent double-submit
    setIsSaving(true);

    // CAPTURE FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    capturedAddReaderFormValuesRef.current = { ...addReaderForm.state.values };
    const captured = capturedAddReaderFormValuesRef.current;

    // Create a modified chart with speaker set to 'reader' so getUnattestedScript generates the correct script
    // (getUnattestedScript reads speaker from chart.SCRIPT_REVIEW.speaker)
    const chartWithReader = JSON.parse(JSON.stringify(chart)) as Chart;
    chartWithReader.SCRIPT_REVIEW = {
      ...chartWithReader.SCRIPT_REVIEW,
      speaker: 'reader',
      reader: {
        email: captured.email,
        fullName: captured.fullName,
        phoneNumber: captured.phoneNumber,
      },
    };

    // Generate script with reader as speaker
    const newScript = getUnattestedScript({
      code: localCode as keyof typeof TreatmentOptions,
      chart: chartWithReader,
      patientInfo,
    });

    // STEP 1: Update SCRIPT_REVIEW with reader info
    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: chart.SCRIPT_REVIEW?.scriptAttested || false,
            speaker: 'reader',
            reader: {
              email: captured.email,
              fullName: captured.fullName,
              phoneNumber: captured.phoneNumber,
            },
            readerIdentificationFrontURI: chart.SCRIPT_REVIEW?.readerIdentificationFrontURI || '',
            telePrompterScript: newScript,
          },
        },
      },
      {
        onSuccess: () => {
          // STEP 2: Update SELECTED_TREATMENT_OPTION with new script
          mutate(
            {
              patientId: patientId,
              docData: {
                SELECTED_TREATMENT_OPTION: {
                  code: localCode as keyof typeof TreatmentOptions,
                  description: chart.SELECTED_TREATMENT_OPTION?.description || '',
                  unAttestedScript: newScript,
                },
              },
            },
            {
              onSuccess: () => {
                setReaderSubmitted(true);
                setIsSaving(false);
              },
              onError: (error) => {
                Sentry.captureException(error);
                alert(`Error updating document: ${error.message}`);
                console.error(error);
                setIsSaving(false);
              },
            },
          );
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`);
          console.error(error);
          setIsSaving(false);
        },
      },
    );
  };

  // INITIALIZE ADD READER FORM
  // Reader declaration now lives ONLY on this screen (the HCA-screen reader
  // checkbox was removed in CU-86aha0x95). When no reader has been saved yet,
  // pre-populate from the named healthcare agent so the default reader IS the
  // HCA — the patient can still edit. A previously-saved reader is never
  // clobbered: HCA values are only used as a fallback when the SCRIPT_REVIEW
  // reader field is empty.
  const hcaInfo = chart?.HEALTHCARE_AGENT_INFO;
  const addReaderForm = useForm({
    defaultValues: {
      email: chart?.SCRIPT_REVIEW?.reader?.email || hcaInfo?.email || '',
      fullName: chart?.SCRIPT_REVIEW?.reader?.fullName || hcaInfo?.agent || '',
      phoneNumber:
        chart?.SCRIPT_REVIEW?.reader?.phoneNumber || hcaInfo?.primaryPhone || hcaInfo?.cellPhone || '',
      readerIdentificationFrontURI: chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI
        ? chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
        : '',
    },
    onSubmit: () => {
      handleAddReader();
    },
  });

  const handleReaderCheckboxClick = async () => {
    if (isSaving) return; // Prevent interaction during save
    if (hasReader) {
      // handleRemoveReader will set hasReader to false in onSuccess
      await handleRemoveReader();
    } else {
      setHasReader(true);
    }
  };

  // SET SELECTED_TREATMENT_OPTION TO FULL CODE FOR SELF-GUIDED PATIENTS IF IT DOESN'T EXIST (OR IF CREATED EMPTY BY BACKEND)
  // ONCE PER FAILURE, NOT ONCE PER SNAPSHOT. THE CLICK HANDLERS CLEAR THIS DOC ON PURPOSE AND RELY ON THIS
  // TO RE-CREATE IT: A SUCCESS RE-ARMS THE LATCH, AND clearSelectedTreatmentOption CALLS rearm() AFTER THE CLEAR.
  const { rearm: rearmSelectedTreatmentOption } = useInitChartSection({
    needed:
      !!patientInfo && patientInfo.navType?.base === 'self' && !!chart && !chart.SELECTED_TREATMENT_OPTION?.unAttestedScript,
    write: () =>
      mutateAsync({
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: localCode as keyof typeof TreatmentOptions,
            description: 'Full Code - All Treatment & No Limitations in Care',
            unAttestedScript: getUnattestedScript({
              code: 'fullCode',
              chart: chart,
              patientInfo,
            }),
          },
        },
      }),
  });

  // CLEAR SELECTED_TREATMENT_OPTION SO THE INIT ABOVE RE-CREATES IT WITH A FRESH SCRIPT. THIS IS THE FIX FOR "PATIENT
  // CANNOT ATTEST TO SCRIPT AFTER CHANGES". AN OVERWRITE, NEVER A DELETE: CHART DOCS ARE NEVER DELETABLE, AND THE
  // FIRESTORE RULES REFUSE IT (CU-17tm466r238). createEmptyFirestoreDoc BYPASSES useUpdateChartSection, SO A FAILURE
  // IS REPORTED HERE (SAME TAGS). ON FAILURE THE CALLER STOPS: NO rearm(), NO SCRIPT_REVIEW WRITE, AND isSaving IS
  // RESET SO THE CONTROL STAYS USABLE. RETURNS WHETHER THE CLEAR LANDED.
  const clearSelectedTreatmentOption = async (): Promise<boolean> => {
    try {
      await createEmptyFirestoreDoc({ patientId, title: 'SELECTED_TREATMENT_OPTION' });
    } catch (error) {
      Sentry.captureException(error, { tags: { chartDoc: 'SELECTED_TREATMENT_OPTION', chartWrite: 'chart' } });
      notifyChartSaveError();
      setIsSaving(false);
      return false;
    }
    // ITS useInitChartSection RE-CREATES IT; rearm() SO IT ALSO DOES AFTER AN EARLIER FAILED INIT
    rearmSelectedTreatmentOption();
    return true;
  };

  // WRITE SCRIPT ERROR FORM DATA TO FIRESTORE
  // NOTE: MUTATIONS RUN SEQUENTIALLY TO ENSURE onSuccess CALLBACKS FIRE CORRECTLY.
  // PARALLEL MUTATIONS ON THE SAME useMutation HOOK CAUSE CALLBACK OVERWRITES.
  const handleScriptError = async () => {
    if (isSaving) return; // Prevent double-submit
    setIsSaving(true);

    // CAPTURE FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    capturedScriptErrorFormValuesRef.current = { ...scriptErrorForm.state.values };
    const captured = capturedScriptErrorFormValuesRef.current;

    // NORMALIZE PHONE NUMBER TO E.164 FORMAT BEFORE SAVING
    const agentPhoneE164 = formatPhoneE164(captured.agentHomePhone);
    const agentPhoneValue = agentPhoneE164 || captured.agentHomePhone;

    // DEEP CLONE CHART SO THAT IT CAN BE MODIFIED WITH FORM DATA (THEN SUBMITTED TO GENERATE NEW SCRIPTS)
    // Deep clone prevents accidental mutation of the original chart object
    const chartClone = JSON.parse(JSON.stringify(chart)) as Chart;
    chartClone.PATIENT_INFO_1.firstName = captured.firstName;
    chartClone.PATIENT_INFO_1.middleInitial = captured.middleInitial;
    chartClone.PATIENT_INFO_1.lastName = captured.lastName;
    chartClone.PATIENT_INFO_1.DOB = formatDateForStorage(captured.DOB);
    chartClone.HEALTHCARE_AGENT_INFO.agent = captured.agent;
    chartClone.HEALTHCARE_AGENT_INFO.agentToPatient = captured.agentToPatient;
    // DUAL-WRITE TO BOTH NEW AND LEGACY PHONE FIELDS
    chartClone.HEALTHCARE_AGENT_INFO.primaryPhone = agentPhoneValue;
    chartClone.HEALTHCARE_AGENT_INFO.cellPhone = agentPhoneValue;
    chartClone.HEALTHCARE_AGENT_INFO.patientToAgent = chart.HEALTHCARE_AGENT_INFO.patientToAgent;

    // STEP 1: UPDATE PATIENT_INFO_1 FIRESTORE DOCUMENT WITH FORM DATA
    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_1: {
            firstName: captured.firstName,
            middleInitial: captured.middleInitial,
            gender: chart.PATIENT_INFO_1.gender ? chart.PATIENT_INFO_1.gender : '',
            lastName: captured.lastName,
            DOB: formatDateForStorage(captured.DOB),
            selfReporting: chart.PATIENT_INFO_1.selfReporting ? chart.PATIENT_INFO_1.selfReporting : true,
          },
        },
      },
      {
        onSuccess: () => {
          const captured = capturedScriptErrorFormValuesRef.current;
          if (!captured) {
            setIsSaving(false);
            return;
          }
          // SYNC displayName TO USER DOC FOR ADMIN CONSOLE SEARCH
          const firstName = captured.firstName?.trim() || '';
          const lastName = captured.lastName?.trim() || '';
          const displayName = `${firstName} ${lastName}`.trim();
          if (displayName) {
            mutatePatientDoc(
              { patientId, displayName },
              {
                onSuccess: () => {
                  // displayName synced, continue chain
                  handleScriptErrorStep2(chartClone);
                },
                onError: (error) => {
                  Sentry.captureException(error);
                  console.error('Error updating displayName:', error);
                  // CONTINUE CHAIN EVEN IF displayName FAILS
                  handleScriptErrorStep2(chartClone);
                },
              },
            );
          } else {
            handleScriptErrorStep2(chartClone);
          }
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
          setIsSaving(false);
        },
      },
    );
  };

  // STEP 2: UPDATE HEALTHCARE_AGENT_INFO FIRESTORE DOCUMENT WITH FORM DATA
  const handleScriptErrorStep2 = (chartClone: Chart) => {
    const captured = capturedScriptErrorFormValuesRef.current;
    if (!captured) {
      setIsSaving(false);
      return;
    }

    // NORMALIZE PHONE NUMBER TO E.164 FORMAT BEFORE SAVING
    const agentPhoneE164 = formatPhoneE164(captured.agentHomePhone);
    const agentPhoneValue = agentPhoneE164 || captured.agentHomePhone;

    mutate(
      {
        patientId: patientId,
        docData: {
          HEALTHCARE_AGENT_INFO: {
            agent: captured.agent,
            street: chart.HEALTHCARE_AGENT_INFO.street,
            city: chart.HEALTHCARE_AGENT_INFO.city,
            state: chart.HEALTHCARE_AGENT_INFO.state,
            ZIP: chart.HEALTHCARE_AGENT_INFO.ZIP,
            country: chart.HEALTHCARE_AGENT_INFO.country,
            email: chart.HEALTHCARE_AGENT_INFO.email,
            // NEW NORMALIZED FIELD
            primaryPhone: agentPhoneValue,
            // LEGACY FIELDS (FOR BACKWARD COMPATIBILITY)
            cellPhone: agentPhoneValue,
            homePhone: chart.HEALTHCARE_AGENT_INFO.homePhone,
            secondaryPhone: chart.HEALTHCARE_AGENT_INFO.secondaryPhone || chart.HEALTHCARE_AGENT_INFO.homePhone,
            agentToPatient: captured.agentToPatient,
            patientToAgent: chart.HEALTHCARE_AGENT_INFO.patientToAgent,
            hasPowerOfAttorney: chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney
              ? chart.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney
              : 'unset',
          },
        },
      },
      {
        onSuccess: () => {
          handleScriptErrorStep3(chartClone);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
          setIsSaving(false);
        },
      },
    );
  };

  // STEP 3: UPDATE SELECTED_TREATMENT_OPTION FIRESTORE DOCUMENT WITH NEW SCRIPT BASED ON NEW FORM DATA
  const handleScriptErrorStep3 = (chartClone: Chart) => {
    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: localCode as keyof typeof TreatmentOptions,
            description: chart?.SELECTED_TREATMENT_OPTION?.description
              ? chart.SELECTED_TREATMENT_OPTION.description
              : 'Full Code - All Treatment & No Limitations in Care',
            unAttestedScript: getUnattestedScript({
              code: 'fullCode',
              chart: chartClone,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: () => {
          handleScriptErrorStep4(chartClone);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
          setIsSaving(false);
        },
      },
    );
  };

  // STEP 4: UPDATE SCRIPT_REVIEW FIRESTORE DOCUMENT WITH NEW SCRIPT BASED ON NEW FORM DATA TO KEEP THEM IN SYNC
  const handleScriptErrorStep4 = (chartClone: Chart) => {
    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: localCode as keyof typeof TreatmentOptions,
            scriptAttested: false,
            speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            telePrompterScript: getUnattestedScript({
              code: 'fullCode',
              chart: chartClone,
              patientInfo,
            }),
          },
        },
      },
      {
        onSuccess: () => {
          setScripHasError(false);
          setIsSaving(false);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
          setIsSaving(false);
        },
      },
    );
  };

  // INITIALIZE SCRIPT ERROR FORM
  const scriptErrorForm = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1?.firstName : '',
      middleInitial: chart?.PATIENT_INFO_1?.middleInitial ? chart.PATIENT_INFO_1?.middleInitial : '',
      lastName: chart?.PATIENT_INFO_1?.lastName ? chart.PATIENT_INFO_1?.lastName : '',
      DOB: chart?.PATIENT_INFO_1?.DOB ? formatDateForCalendar(chart?.PATIENT_INFO_1?.DOB) : '',
      agent: chart?.HEALTHCARE_AGENT_INFO?.agent ? chart.HEALTHCARE_AGENT_INFO?.agent : '',
      agentToPatient: chart?.HEALTHCARE_AGENT_INFO?.agentToPatient ? chart.HEALTHCARE_AGENT_INFO?.agentToPatient : '',
      agentHomePhone: chart?.HEALTHCARE_AGENT_INFO?.primaryPhone
        ? chart.HEALTHCARE_AGENT_INFO?.primaryPhone
        : chart?.HEALTHCARE_AGENT_INFO?.cellPhone
          ? chart.HEALTHCARE_AGENT_INFO?.cellPhone
          : '',
    },
    onSubmit: () => {
      handleScriptError();
    },
  });

  // SET READER SUBMITTED FROM CHART
  React.useEffect(() => {
    if (
      chart?.SCRIPT_REVIEW?.reader &&
      chart.SCRIPT_REVIEW?.reader.email?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.fullName?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.phoneNumber?.length > 0 &&
      chart.SCRIPT_REVIEW?.reader.email == addReaderForm?.state.values.email &&
      chart.SCRIPT_REVIEW?.reader.fullName == addReaderForm?.state.values.fullName &&
      chart.SCRIPT_REVIEW?.reader.phoneNumber == addReaderForm?.state.values.phoneNumber
    ) {
      setReaderSubmitted(true);
    }
  }, [chart, addReaderForm]);

  // // CREATE FULL CODE SCRIPT REVIEW DOC IN FIRESTORE FOR A SELF-GUIDED PATIENT
  // const handleGenerateScript = React.useCallback(() => {
  //   if (!chart) {
  //     // setAttested(false);
  //     console.error('Exiting due to lack of chart');
  //     return;
  //   } else if (
  //     chart &&
  //     chart.PATIENT_INFO_1 &&
  //     chart.LIVING_WILL &&
  //     chart.HEALTHCARE_AGENT_INFO &&
  //     patientInfo?.navType?.base === 'self'
  //   ) {
  //     const newScript = getUnattestedScript({ code: 'fullCode', chart, patientInfo });
  //     // if (newScript == chart.SCRIPT_REVIEW?.telePrompterScript) {
  //     if (newScript == chart.SELECTED_TREATMENT_OPTION?.unAttestedScript) {
  //       // IF THE SCRIPT GENERATED WITH THE NEW CHART IS THE SAME, DO NOTHING
  //       return;
  //     } else {
  //       mutate(
  //         {
  //           patientId: patientId,
  //           docData: {
  //             SCRIPT_REVIEW: {
  //               attestedCode: 'fullCode',
  //               scriptAttested: false,
  //               telePrompterScript: newScript,
  //               speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
  //             },
  //           },
  //         },
  //         {
  //           onSuccess: () => {
  //             // alert("Successfully updated document");
  //             setScripHasError(false);
  //           },
  //           onError: (error) => {
  //             Sentry.captureException(error);
  //             alert(`Error updating document: ${error.message}`); // TELL THE USER
  //             console.error(error); // GET THE STACKTRACE
  //           },
  //         },
  //       );
  //     }
  //   } else {
  //     console.error('Exiting due to lack of patient info or chart');
  //     return;
  //   }
  // }, [chart, patientInfo, patientId, mutate]);

  // createEmptyFirestoreDoc BYPASSES useUpdateChartSection, SO NOTHING ELSE REPORTS ITS FAILURE.
  // SAME TAGS AS useUpdateChartSection, SO ONE SENTRY SEARCH (has:chartDoc) FINDS EVERY CHART-WRITE FAILURE.
  const reportSignatureInitError = (chartDoc: 'PATIENT_SIGNATURE' | 'WITNESS_SIGNATURE') => (error: unknown) => {
    Sentry.captureException(error, { tags: { chartDoc, chartWrite: 'chart' } });
    notifyChartSaveError();
  };

  // INITIALIZE EMPTY PATIENT_SIGNATURE FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.PATIENT_SIGNATURE,
    write: () => createEmptyFirestoreDoc({ patientId, title: 'PATIENT_SIGNATURE' }),
    onError: reportSignatureInitError('PATIENT_SIGNATURE'),
  });

  // INITIALIZE EMPTY WITNESS_SIGNATURE FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.WITNESS_SIGNATURE,
    write: () => createEmptyFirestoreDoc({ patientId, title: 'WITNESS_SIGNATURE' }),
    onError: reportSignatureInitError('WITNESS_SIGNATURE'),
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'attestation', patientInfo, value: true });
    }
  }, [patientInfo]);

  const handleAttestationCheckboxClick = async () => {
    if (!chart) return;
    if (isSaving) return; // Prevent double-submit
    setIsSaving(true);

    // Calculate new attested value upfront
    const newAttestedValue = !attested;

    if (patientInfo?.navType?.base === 'self') {
      if (!(await clearSelectedTreatmentOption())) return;
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: localCode as keyof typeof TreatmentOptions,
              scriptAttested: newAttestedValue,
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
              telePrompterScript: getUnattestedScript({
                code: localCode as keyof typeof TreatmentOptions,
                chart,
                patientInfo,
              }),
            },
          },
        },
        {
          onSuccess: () => {
            setIsSaving(false);
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`);
            console.error(error);
            setIsSaving(false);
          },
        },
      );
    } else {
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: localCode as keyof typeof TreatmentOptions,
              scriptAttested: newAttestedValue,
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
              telePrompterScript: chart.SELECTED_TREATMENT_OPTION?.unAttestedScript
                ? chart.SELECTED_TREATMENT_OPTION.unAttestedScript
                : 'There was an error reading your script. Please consult your concierge.',
            },
          },
        },
        {
          onSuccess: () => {
            setIsSaving(false);
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`);
            console.error(error);
            setIsSaving(false);
          },
        },
      );
    }
  };

  React.useEffect(() => {
    if (chart && chart.SCRIPT_REVIEW) {
      setAttested(chart.SCRIPT_REVIEW.scriptAttested);
    }
  }, [chart]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="attestation-page" className="flex flex-col items-center justify-center">
          <Heading id="attestation-page-header" textContent="Attestation Page" />
          <p className="text-xl">
            The box below contains the script you will read when recording your MIDEO safety statement. Please read it
            in its entirety before attesting.
          </p>
          <div id="attestation-page-main" className="mt-4 flex flex-row justify-around">
            <div id="attestation-page-content" className="flex flex-col">
              <>
                <div id="script-container" className="w-full p-4 text-2xl outline">
                  <p className="text-pretty">
                    {chart.SCRIPT_REVIEW?.telePrompterScript || chart.SELECTED_TREATMENT_OPTION?.unAttestedScript}
                  </p>
                </div>
                <div id="reader-checkbox-container" className="my-4 flex w-full flex-col">
                  {patientInfo?.navType?.base === 'self' && (
                    <Checkbox
                      id="reader-checkbox"
                      name="reader"
                      disabled={isSaving}
                      onChange={() => {
                        handleReaderCheckboxClick();
                      }}
                      checked={hasReader}
                      label={`I am unable to read the script myself. I have asked someone to read it for me today.`}
                    />
                  )}
                  {hasReader && patientInfo?.navType?.base === 'self' && (
                    <div id="add-reader-form-container" className="flex flex-col pl-4">
                      <h2 className="text-xl font-bold">
                        NOTE: When filming with a reader, the patient should introduce the reader if possible. The
                        patient must also be visible for the duration of the recording. This does not apply if the
                        person reading for the patient is the patient&apos;s legally appointed healthcare agent.
                      </h2>
                      <form id="add-reader-form" className="flex flex-col">
                        <addReaderForm.Field
                          name="fullName"
                          validators={{
                            onChange({ value }) {
                              if (!value || value.length === 0) {
                                return "Reader's name cannot be blank.";
                              } else {
                                return undefined;
                              }
                            },
                          }}
                          children={(field) => (
                            <>
                              <FormInput
                                label="Full Name*"
                                id="reader-full-name"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => {
                                  setReaderSubmitted(false);
                                  field.handleChange(e.target.value);
                                }}
                                required={true}
                              />
                              <FieldInfo fieldMeta={field.state.meta} />
                            </>
                          )}
                        />
                        <addReaderForm.Field
                          name="email"
                          validators={{
                            onChange({ value }) {
                              if (!value || value.length === 0) {
                                return "Reader's email address cannot be blank.";
                              } else {
                                return undefined;
                              }
                            },
                          }}
                          children={(field) => (
                            <>
                              <FormInput
                                label="Email*"
                                id="reader-email"
                                name={field.name}
                                value={field.state.value}
                                handleBlur={field.handleBlur}
                                handleChange={(e) => {
                                  setReaderSubmitted(false);
                                  field.handleChange(e.target.value);
                                }}
                                required={true}
                              />
                              <FieldInfo fieldMeta={field.state.meta} />
                            </>
                          )}
                        />
                        <div className="mt-2">
                          <h2 className="text-xl font-bold">Reader&apos;s Cell Phone*</h2>
                          <addReaderForm.Field
                            name="phoneNumber"
                            validators={{
                              onChange: ({ value }) => {
                                if (!value) return "Reader's cell phone is required";
                                const result = validatePhone(value);
                                return result.isValid ? undefined : result.error;
                              },
                            }}
                            children={(field) => (
                              <>
                                <PhoneInput
                                  international
                                  defaultCountry="US"
                                  placeholder="Enter phone number"
                                  value={field.state.value ? field.state.value : ''}
                                  onChange={(value: string | undefined) => {
                                    setReaderSubmitted(false);
                                    field.handleChange(value || '');
                                  }}
                                />
                                <FieldInfo fieldMeta={field.state.meta} />
                              </>
                            )}
                          />
                          <div id="reader-id-container" className="mt-4 flex flex-col">
                            <p className="text-2xl font-bold">Reader ID Front</p>
                            {chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI &&
                            takingPhoto.readerIdentificationFront === false ? (
                              <div className="flex flex-row items-center">
                                <img
                                  src={chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI}
                                  style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                                  alt="Reader ID Front"
                                />
                                <div className="ml-4">
                                  <Button
                                    id="retake-reader-id-front"
                                    type="button"
                                    textContent="Retake Photo"
                                    onClick={() => {
                                      setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              takingPhoto.readerIdentificationFront === false && (
                                <Button
                                  id="take-reader-id-front"
                                  type="button"
                                  textContent="Take Photo"
                                  onClick={() => {
                                    setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                                  }}
                                />
                              )
                            )}
                            {takingPhoto.readerIdentificationFront && (
                              <PhotoCamera2
                                type={FileTypes.readerIdFront}
                                docToUpdate="SCRIPT_REVIEW"
                                style={'card'}
                                bucket={'patient'}
                                path={`user/${patientId}/images/readerIdentificationFront`}
                                patientId={patientId}
                                hideCamera={() => {
                                  setPhotoOneWorking(false);
                                  setTakingPhoto({ ...takingPhoto, readerIdentificationFront: false });
                                }}
                                working={photoOneWorking}
                                setWorking={setPhotoOneWorking}
                              />
                            )}
                          </div>
                          <div className="mt-8">
                            <addReaderForm.Subscribe
                              selector={(state) => [state.canSubmit, state.errors]}
                              children={([canSubmit]) => (
                                <>
                                  {!canSubmit && (
                                    <div className="mt-4 flex flex-col items-center">
                                      <p className="text-red-500">
                                        Please correct any errors above before re-submitting.
                                      </p>
                                    </div>
                                  )}
                                  {!readerSubmitted && (
                                    <Button
                                      id="submit-reader-info"
                                      type="submit"
                                      disabled={!canSubmit || !chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI || isSaving}
                                      onClick={() => {
                                        addReaderForm.handleSubmit();
                                      }}
                                      submitted={readerSubmitted}
                                      textContent={isSaving ? 'Saving...' : 'Add Reader'}
                                    />
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
                <div id="attestation-checkbox-container" className="my-4 flex w-full flex-row justify-center">
                  <Checkbox
                    id="attestation-checkbox"
                    name="attestation"
                    disabled={isSaving}
                    onChange={() => {
                      handleAttestationCheckboxClick();
                    }}
                    checked={attested}
                    label={`I attest that I have reviewed and approve of this script
                    ${patientInfo.navType?.base !== 'self' ? 'including any edits' : ''} ${chart.SCRIPT_REVIEW?.speaker !== 'patient' ? "and the selected treatment choice. Once attested, a reader's script will be shown if requested." : ''}`}
                  />
                </div>
              </>
            </div>
          </div>
          {attested === false && patientInfo?.navType?.base === 'self' && (
            <Button
              id="report-error"
              type="button"
              textContent="There is an error in my script"
              onClick={() => setScripHasError(!scripHasError)}
              disabled={isSaving}
            />
          )}
          {scripHasError && attested === false && (
            <>
              <form id="error-report-form" className="flex flex-col">
                <scriptErrorForm.Field
                  name="firstName"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return 'First name cannot be blank.';
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="First Name*"
                        id="first-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="middleInitial"
                  children={(field) => (
                    <>
                      <FormInput
                        label="Middle Initial"
                        id="middle-initial"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={false}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="lastName"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return 'Last name cannot be blank.';
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Last Name*"
                        id="last-name"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="DOB"
                  validators={{
                    onChange: ({ value }) => validateAdultDob(value),
                  }}
                  children={(field) => (
                    <div className="scheme-dark">
                      <FormInput
                        label="DOB* (MM/DD/YYYY)"
                        id="dob"
                        type="date"
                        name={field.name}
                        value={field.state.value}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <p className="text-xl font-bold text-green-900 italic">
                        *Please confirm patient DOB matches government ID before submitting.
                      </p>
                      <FieldInfo fieldMeta={field.state.meta} />
                    </div>
                  )}
                />
                <scriptErrorForm.Field
                  name="agent"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return "Agent's name cannot be blank.";
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Agent's Full Name*"
                        id="agent-name"
                        name={field.name}
                        value={field.state.value ? field.state.value : ''}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <scriptErrorForm.Field
                  name="agentToPatient"
                  validators={{
                    onChange({ value }) {
                      if (!value || value.length === 0) {
                        return "Agent's relationship to patient cannot be blank.";
                      } else {
                        return undefined;
                      }
                    },
                  }}
                  children={(field) => (
                    <>
                      <FormInput
                        label="Agent's Relationship To Patient*"
                        id="agent-to-patient"
                        placeholder='This person is my "___"'
                        name={field.name}
                        value={field.state.value ? field.state.value : ''}
                        handleBlur={field.handleBlur}
                        handleChange={(e) => field.handleChange(e.target.value)}
                        required={true}
                      />
                      <FieldInfo fieldMeta={field.state.meta} />
                    </>
                  )}
                />
                <div className="mt-2">
                  <h2 className="text-xl font-bold">Agent&apos;s Primary Phone*</h2>
                  <scriptErrorForm.Field
                    name="agentHomePhone"
                    validators={{
                      onChange: ({ value }) => {
                        if (!value) return "Agent's primary phone is required";
                        const result = validatePhone(value);
                        return result.isValid ? undefined : result.error;
                      },
                    }}
                    children={(field) => (
                      <>
                        <PhoneInput
                          international
                          defaultCountry="US"
                          placeholder="Enter phone number"
                          value={field.state.value ? field.state.value : ''}
                          onChange={(value: string | undefined) => field.handleChange(value || '')}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <scriptErrorForm.Subscribe
                    selector={(state) => [state.canSubmit, state.errors]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-update-info"
                          type="submit"
                          disabled={!canSubmit || isSaving}
                          onClick={() => {
                            scriptErrorForm.handleSubmit();
                          }}
                          textContent={isSaving ? 'Updating...' : 'Update Script'}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </>
          )}
          <div id="nav" className="flex flex-col items-center">
            {patientInfo?.navType?.base === 'self' && (
              <Button
                id="submit-info"
                type="button"
                textContent="Submit"
                onClick={() => setIsSubmitted(true)}
                disabled={
                  isSaving ||
                  !chart?.SCRIPT_REVIEW?.telePrompterScript ||
                  !attested ||
                  (hasReader && (!readerSubmitted || !chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI))
                }
                submitted={isSubmitted}
              />
            )}
            {attested && patientInfo.navType?.base === 'self' ? (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={isSaving || !attested || !isSubmitted}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={isSaving || !attested}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
